import { usePublicLanguage } from '../public-site/PublicLanguage';
import React from 'react';
import PublicLayout, { PublicThemeProps } from '../public-site/PublicLayout';

export const FAQ_TITLE = 'FAQ | Revelationary';
export const FAQ_DESCRIPTION =
  'Answers to common questions about reading the Bible, translations, free discussions and study tools on Revelationary.';

export const FAQ_ITEMS = [
  {
    question: 'Is Revelationary Online free?',
    answer:
      'Reading and searching the Bible are free, with no account required. Commenting and replying are also free with an account. Optional paid monthly and yearly plans are available, but you do not need a subscription to join discussions or use the current study tools.',
  },
  {
    question: 'Are paid plans available, and what do they include?',
    answer:
      'Yes. The membership page currently lists a monthly plan at £1.99 per month and a yearly plan at £19.99 per year, with a seven-day free trial. Payments and subscription management use Stripe. The current study tools, including search, highlights and reading progress, are also available without a paid subscription; subscribing does not currently unlock a separate set of exclusive tools. Review the price and trial terms shown at checkout before subscribing.',
  },
  {
    question: 'How do I study the Bible online for free?',
    answer:
      'Open the Bible study tool to start reading and searching without signing up. Create a free account to comment, reply and save your highlights across sessions and devices. You can choose a Bible translation before you begin.',
  },
  {
    question: 'What Bible translations are available online?',
    answer:
      'Revelationary Online supports multiple Bible translations including King James Version (KJV) and Free Bible Version (FBV) in English, Chinese Union Version (CUV) in Simplified Chinese, Lutherbibel (LUT) in German, Reina Valera 1909 (RV09) in Spanish, Diodati 1885 in Italian, Bible kralická (BKR) in Czech, Svenska Bibeln (SKB) in Swedish, Alkitab Indonesia (TSI) in Indonesian, Nederlandse Bijbel 1939 (NBG) in Dutch, and Kinh Thánh Việt (OVCB) in Vietnamese. All translations are available for free online Bible study.',
  },
  {
    question: 'What are the best online Bible study tools?',
    answer:
      "The best online Bible study tools include verse highlighting, Strong's Concordance integration, community comments and discussions, reading progress tracking, bookmarks, verse search, and Bible Project video summaries. Revelationary Online brings these tools together in its Bible reader.",
  },
  {
    question: 'Can I study the Bible online with a group?',
    answer:
      "Yes, you can study the Bible online with others through Revelationary Online's community features. You can comment on Bible verses, reply to other users' comments, and engage in discussions about scripture. The platform enables collaborative online Bible study.",
  },
  {
    question: 'How does online Bible study work?',
    answer:
      'Online Bible study with Revelationary Online works by providing interactive tools to read, highlight, and study scripture. You can search for specific verses, read multiple translations, leave comments, track your reading progress, and watch video summaries. Everything is accessible through your web browser for convenient online Bible study.',
  },
  {
    question: 'Can I comment on Bible verses?',
    answer:
      'Yes, you can comment on any Bible verse, reply to other comments, and engage in discussions with the community. This feature allows you to share insights, ask questions, and learn from others during your online Bible study.',
  },
  {
    question: 'How do I track my Bible reading progress?',
    answer:
      'The app automatically tracks your reading progress as you navigate through chapters. You can mark chapters as read to track your progress through the entire Bible. This feature helps you maintain consistency in your online Bible study routine.',
  },
  {
    question: 'Are there study guides available for online Bible study?',
    answer:
      'Yes, Revelationary Online includes Bible Project video summaries for many books and chapters, providing visual overviews and explanations. These videos serve as study guides to help you understand the context and themes of each biblical book during your online Bible study.',
  },
  {
    question: 'Is online Bible study as effective as traditional methods?',
    answer:
      "Online Bible study can be highly effective when using the right tools. Revelationary Online combines traditional Bible reading with modern interactive features like verse highlighting, Strong's Concordance, community discussions, and progress tracking, making online Bible study both convenient and comprehensive.",
  },
];

export function faqSchema(
  t: (text: string) => string = (text) => text,
  url = 'https://revelationary.online/faq/',
  language = 'en',
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: language,
    name: t(FAQ_TITLE),
    description: t(FAQ_DESCRIPTION),
    url,
    mainEntity: FAQ_ITEMS.map(({ question, answer }) => ({
      '@type': 'Question',
      name: t(question),
      acceptedAnswer: { '@type': 'Answer', text: t(answer) },
    })),
  };
}

export default function FAQPage(props: PublicThemeProps) {
  const { t, href } = usePublicLanguage();
  return (
    <PublicLayout {...props}>
      <section className="faq-page home-width" aria-labelledby="faq-title">
        <header>
          <h1 id="faq-title">/faq</h1>
          <p>{t('Answers to your questions about reading and studying with Revelationary.')}</p>
        </header>
        <div className="faq-questions">
          {FAQ_ITEMS.map(({ question, answer }, index) => (
            <details key={question} id={`faq-${index + 1}`}>
              <summary>{t(question)}</summary>
              <p>{t(answer)}</p>
            </details>
          ))}
        </div>
        <p className="faq-contact">
          <a href={href('/study/#/subscribe')}>{t('View paid plans')}</a>{' '}
          {t('· Still have questions?')} <a href={href('/contact/')}>{t('Get in touch →')}</a>
        </p>
      </section>
    </PublicLayout>
  );
}
